import React, { FC, PropsWithChildren, useRef } from 'react';
import { useDrag, useDrop, DropTargetMonitor } from 'react-dnd';
import { XYCoord } from 'dnd-core';
import { Paper as MuiPaper, styled } from '@mui/material';

import { DragItem, QuestionCardType } from '../../models/FormBuilderProps';

const Paper = styled(MuiPaper)(({ theme }) => ({
  padding: '12px 24px',
  border: '0.5px solid',
  borderColor: theme.palette.divider,
  marginBottom: '24px',
  boxShadow: theme.shadows[2],
  cursor: 'pointer',
}));

export interface DragQuestionCardProps {
  item: QuestionCardType;
  index: number;
  moveCard: (dragIndex: number, hoverIndex: number) => void;
}

const DragQuestionCard: FC<PropsWithChildren<DragQuestionCardProps>> = ({
  item,
  index,
  moveCard,
  children,
}) => {
  const ref = useRef<HTMLDivElement>(null);

  const [{ handlerId }, drop] = useDrop({
    accept: 'card',
    collect(monitor) {
      return {
        handlerId: monitor.getHandlerId(),
      };
    },
    hover(item: DragItem, monitor: DropTargetMonitor) {
      if (!ref.current) {
        return;
      }
      const dragIndex = item.index;
      const hoverIndex = index;

      if (dragIndex === hoverIndex) {
        return;
      }

      const hoverBoundingRect = ref.current?.getBoundingClientRect();
      const hoverMiddleY =
        (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
      const clientOffset = monitor.getClientOffset();
      const hoverClientY = (clientOffset as XYCoord).y - hoverBoundingRect.top;

      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
        return;
      }

      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
        return;
      }

      moveCard(dragIndex, hoverIndex);
      item.index = hoverIndex;
    },
  });

  const [{ isDragging }, drag] = useDrag({
    type: 'card',
    item: () => {
      return { ...item, index };
    },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  drag(drop(ref));

  return (
    <Paper
      ref={ref}
      sx={{ opacity: isDragging ? 0 : 1 }}
      data-handler-id={handlerId}
    >
      {children}
    </Paper>
  );
};

export default DragQuestionCard;
